"use client";

import { ChevronDown, Code, Layers, Menu, Shield, X, Zap } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { trackLeadLogin, trackLeadSignup } from "@/lib/analytics";

const navItems = [
  {
    label: "Product",
    href: "/product",
    hasDropdown: true,
    dropdownItems: [
      {
        label: "Features",
        href: "/features",
        icon: Layers,
        description: "Visual workflow builder",
      },
      {
        label: "Integrations",
        href: "/integrations",
        icon: Zap,
        description: "Connect any API",
      },
      {
        label: "Security",
        href: "/security",
        icon: Shield,
        description: "Enterprise-grade",
      },
      {
        label: "For Developers",
        href: "/developers",
        icon: Code,
        description: "Full API access",
      },
    ],
  },
  { label: "Templates", href: "/templates" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQ", href: "/faq" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownCloseTimerRef = useRef<number | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  const cancelDropdownClose = () => {
    if (dropdownCloseTimerRef.current !== null) {
      window.clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
  };

  const scheduleDropdownClose = () => {
    cancelDropdownClose();
    dropdownCloseTimerRef.current = window.setTimeout(() => {
      setActiveDropdown(null);
    }, 120);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActiveRoute = (href: string) => {
    if (href.includes("#")) {
      const [path] = href.split("#");
      return pathname === path;
    }

    return pathname === href;
  };

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    // Check if it's an anchor link to the product page
    if (href.includes("#")) {
      e.preventDefault();
      const [path, hash] = href.split("#");

      if (pathname === path) {
        // Already on the page, just scroll
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // Navigate to the page first, then scroll after a short delay
        router.push(path);
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      }
      setActiveDropdown(null);
    }
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{ paddingRight: "var(--removed-body-scroll-bar-size, 0px)" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        isScrolled
          ? "bg-background/85 backdrop-blur-md border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            {/* <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="w-4 h-4 text-primary-foreground"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <title>Makeplans</title>
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div> */}
            <Image
              src="/logo/logo.png"
              alt="Makeplans"
              width={20}
              height={20}
              className="size-5 rounded-sm object-contain"
            />
            <span className="text-lg font-semibold text-foreground">
              Makeplans
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div key={item.label} className="relative">
                <Link
                  href={item.href}
                  onMouseEnter={() => {
                    if (!item.hasDropdown) return;
                    cancelDropdownClose();
                    setActiveDropdown(item.label);
                  }}
                  onMouseLeave={() => {
                    if (!item.hasDropdown) return;
                    scheduleDropdownClose();
                  }}
                  onFocus={() => {
                    if (!item.hasDropdown) return;
                    cancelDropdownClose();
                    setActiveDropdown(item.label);
                  }}
                  onBlur={() => {
                    if (!item.hasDropdown) return;
                    scheduleDropdownClose();
                  }}
                  className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                    isActiveRoute(item.href)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.label}
                  {item.hasDropdown && (
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${activeDropdown === item.label ? "rotate-180" : ""}`}
                    />
                  )}
                </Link>

                {/* Dropdown Menu */}
                {item.hasDropdown && (
                  <AnimatePresence>
                    {activeDropdown === item.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 pt-2 w-64"
                        onMouseEnter={cancelDropdownClose}
                        onMouseLeave={scheduleDropdownClose}
                      >
                        <div className="bg-card border border-border rounded-xl shadow-xl p-2">
                          {item.dropdownItems?.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.label}
                              href={dropdownItem.href}
                              onClick={(e) =>
                                handleNavClick(e, dropdownItem.href)
                              }
                              className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted transition-colors group"
                            >
                              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                <dropdownItem.icon className="w-4 h-4 text-primary" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  {dropdownItem.label}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {dropdownItem.description}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Keyboard shortcut hint */}
            {/* <div className="hidden xl:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/50 border border-border/50 text-xs text-muted-foreground">
              <Command className="w-3 h-3" />
              <span>K</span>
            </div> */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                trackLeadLogin("landing_header");
                router.push("/login");
              }}
            >
              Sign in
            </Button>
            <Button
              size="sm"
              onClick={() => {
                trackLeadSignup("landing_header");
                router.push("/register");
              }}
            >
              Start free
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-foreground/80 hover:text-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-b border-border overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 space-y-1">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.hasDropdown ? (
                    <div className="space-y-1">
                      <Link
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                          isActiveRoute(item.href)
                            ? "text-primary bg-primary/10"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                      >
                        {item.label}
                      </Link>
                      {/* Mobile dropdown items */}
                      <div className="ml-4 space-y-1">
                        {item.dropdownItems?.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.label}
                            href={dropdownItem.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                          >
                            <dropdownItem.icon className="w-4 h-4 text-primary/70" />
                            <div>
                              <span className="font-medium">
                                {dropdownItem.label}
                              </span>
                              <p className="text-xs text-muted-foreground/70">
                                {dropdownItem.description}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        isActiveRoute(item.href)
                          ? "text-primary bg-primary/10"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 space-y-2 border-t border-border mt-2">
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    trackLeadLogin("landing_header_mobile");
                    router.push("/login");
                  }}
                >
                  Sign in
                </Button>
                <Button
                  className="w-full"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    trackLeadSignup("landing_header_mobile");
                    router.push("/register");
                  }}
                >
                  Start free
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
